import React, { useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { useBooking } from '../context/BookingContext';
import { getCityName, getCountryName, getBusStation, formatDuration } from '../data/routes';
import SearchForm from '../components/SearchForm';

// Mini route map for hero — with realistic Baltic coastline
const MiniRouteMap = ({ fromName, toName }) => (
  <svg viewBox="0 0 220 220" className="w-full opacity-80">
    {/* Simplified Baltic coastline */}
    <path d="M70,15 Q90,8 110,15 L130,12 Q150,8 170,15 L180,30 Q185,50 178,70 L182,100 Q180,130 165,150 L150,170 Q130,188 110,195 L90,190 Q70,180 58,165 L48,140 Q40,115 45,95 L40,70 Q38,45 48,30 Z"
      fill="rgba(255,255,255,0.06)" stroke="rgba(255,255,255,0.18)" strokeWidth="1"/>
    {/* Route path — curved line between cities */}
    <path d="M110,42 Q115,60 112,80 Q108,100 110,120 Q112,140 108,155"
      fill="none" stroke="rgba(255,255,255,0.4)" strokeWidth="1.8" strokeDasharray="5,3"/>
    {/* From city */}
    <circle cx="110" cy="38" r="6" fill="none" stroke="#c30909" strokeWidth="2"/>
    <circle cx="110" cy="38" r="2.5" fill="#c30909"/>
    <text x="110" y="26" textAnchor="middle" fill="white" fontSize="11" fontWeight="700" fontFamily="Inter,sans-serif">{fromName.toUpperCase()}</text>
    {/* To city */}
    <circle cx="108" cy="160" r="6" fill="none" stroke="#c30909" strokeWidth="2"/>
    <circle cx="108" cy="160" r="2.5" fill="#c30909"/>
    <text x="108" y="180" textAnchor="middle" fill="white" fontSize="11" fontWeight="700" fontFamily="Inter,sans-serif">{toName.toUpperCase()}</text>
    {/* Country labels */}
    <text x="165" y="55" fill="rgba(255,255,255,0.2)" fontSize="7" fontWeight="600" letterSpacing="2">ЭСТОНИЯ</text>
    <text x="160" y="115" fill="rgba(255,255,255,0.2)" fontSize="7" fontWeight="600" letterSpacing="2">ЛАТВИЯ</text>
    <text x="158" y="165" fill="rgba(255,255,255,0.2)" fontSize="7" fontWeight="600" letterSpacing="2">ЛИТВА</text>
  </svg>
);

// Route-specific descriptions (like originals)
const routeSpecificDesc = {
  'tallinn-hiiumaa': {
    ru: 'Самой большой достопримечательностью Хийумаа считаются самые известные маяки в Эстонии. Также хорошо известны нетронутая природа острова, прекрасный Кярдла, остров Кассари и приятное чувство юмора местных жителей.',
    en: 'The biggest attraction of Hiiumaa is the most famous lighthouses in Estonia. The island is also known for its pristine nature, beautiful Kärdla, Kassari island and the pleasant sense of humor of the locals.',
  },
  'tallinn-tartu': {
    ru: 'Тарту — интеллектуальная столица Эстонии с престижным университетом и живой молодёжной атмосферой. Музеи, научные центры и захватывающие гастрономические впечатления ждут вас.',
    en: 'Tartu is the intellectual capital of Estonia with a prestigious university and lively youthful atmosphere. Museums, science centers and exciting gastronomic experiences await you.',
  },
  'tallinn-riga': {
    ru: 'Рига — жемчужина Балтии с крупнейшей коллекцией зданий в стиле ар-нуво в Европе. Узкие улочки Старого города входят в список ЮНЕСКО, а рядом — модные рестораны и оживлённые кафе.',
    en: 'Riga is the pearl of the Baltics with Europe\'s largest collection of Art Nouveau buildings. The narrow streets of the Old Town are a UNESCO site, with trendy restaurants and lively cafes nearby.',
  },
  'tallinn-narva': {
    ru: 'В Нарве встречаются две культуры. В приграничном городе по обе стороны реки возвышаются две величественные крепости. Нарвские бастионы — главная достопримечательность города.',
    en: 'In Narva, two cultures meet. In this border city, two majestic fortresses rise on both sides of the river. The Narva bastions are the main attraction of the city.',
  },
  'riga-vilnius': {
    ru: 'Вильнюс — столица Литвы с крупнейшим Старым городом в Восточной Европе, объектом ЮНЕСКО. Барочная архитектура, художественные галереи и бурная культурная жизнь.',
    en: 'Vilnius is Lithuania\'s capital with the largest Old Town in Eastern Europe, a UNESCO site. Baroque architecture, art galleries and vibrant cultural life.',
  },
  'vilnius-warsaw': {
    ru: 'Варшава — город контрастов. Восстановленный после войны Старый город — объект ЮНЕСКО, а рядом сверкают небоскрёбы финансового района. Богатая культурная жизнь столицы Польши.',
    en: 'Warsaw is a city of contrasts. The post-war restored Old Town is a UNESCO site, with financial district skyscrapers gleaming nearby.',
  },
};

// Generic route description fallback
const routeDescriptions = {
  ru: (from, to) => `Маршрут ${from} — ${to} обслуживается комфортабельными автобусами Lux Express с бесплатным Wi-Fi, розетками, мультимедийными экранами и горячими напитками.`,
  en: (from, to) => `The ${from} — ${to} route is served by comfortable Lux Express buses with free Wi-Fi, power outlets, multimedia screens and hot drinks.`,
  lv: (from, to) => `Maršrutu ${from} — ${to} apkalpo Lux Express autobusi ar bezmaksas Wi-Fi, rozetēm un multimēdiju ekrāniem.`,
  lt: (from, to) => `Maršrutą ${from} — ${to} aptarnauja Lux Express autobusai su nemokamu Wi-Fi, rozetėmis ir multimedijos ekranais.`,
  pl: (from, to) => `Trasę ${from} — ${to} obsługują autobusy Lux Express z darmowym Wi-Fi, gniazdkami i ekranami multimedialnymi.`,
};

const sectionTitles = {
  stops: { ru: 'Основные остановки', en: 'Main stops', lv: 'Galvenās pieturas', lt: 'Pagrindinės stotelės', pl: 'Główne przystanki' },
  seatTypes: { ru: 'Типы сидений', en: 'Seat types', lv: 'Sēdvietu veidi', lt: 'Sėdynių tipai', pl: 'Typy miejsc' },
  seatDesc: {
    ru: 'Путешествуйте на стандартном кресле Lux Express или наслаждайтесь максимальным комфортом в кресле Relax.',
    en: 'Travel in a standard Lux Express seat or enjoy maximum comfort in a Relax seat.',
    lv: 'Ceļojiet standarta Lux Express krēslā vai izbaudiet maksimālu komfortu Relax krēslā.',
    lt: 'Keliaukite standartinėje Lux Express kėdėje arba mėgaukitės maksimaliu komfortu Relax kėdėje.',
    pl: 'Podróżuj w standardowym fotelu Lux Express lub ciesz się maksymalnym komfortem w fotelu Relax.',
  },
  whyTitle: { ru: 'Почему пассажиры предпочитают Lux Express', en: 'Why passengers prefer Lux Express', lv: 'Kāpēc pasažieri izvēlas Lux Express', lt: 'Kodėl keleiviai renkasi Lux Express', pl: 'Dlaczego pasażerowie wybierają Lux Express' },
  features: {
    ru: ['Время с пользой', 'Комфорт', 'Lux TV', 'Интернет и 220V'],
    en: ['Time well spent', 'Comfort', 'Lux TV', 'Internet & 220V'],
  },
  featuresDesc: {
    ru: [
      'Мы предлагаем нашим пассажирам провести время с пользой и заняться теми делами, на которые обычно не хватает свободных минут.',
      'Наши просторные сиденья с увеличенным местом для ног и кондиционер воздуха обеспечат вам приятную атмосферу на всё время пути.',
      'Индивидуальный мультимедийный экран делает поездку более приятной.',
      'Бесплатный Wi-Fi и розетки рядом с каждым сиденьем помогают оставаться на связи в пути.',
    ],
    en: [
      'We offer our passengers the opportunity to spend their time productively.',
      'Our spacious seats with extra legroom and air conditioning ensure a pleasant atmosphere.',
      'Individual multimedia screens make the journey more enjoyable.',
      'Free Wi-Fi and power outlets next to every seat help you stay connected.',
    ],
  },
  recommendations: { ru: 'Рекомендации для путешественника', en: 'Travel recommendations', lv: 'Ieteikumi ceļotājam', lt: 'Rekomendacijos keliautojui', pl: 'Zalecenia dla podróżnych' },
  recsDesc: {
    ru: 'Мы считаем, что для того, чтобы путешествовать с комфортом важно чувствовать себя в безопасности. Мы работаем над тем, чтобы в наших автобусах были созданы все условия для того, чтобы путешествовать с хорошим настроением.',
    en: 'We believe that to travel in comfort it is important to feel safe. We work to ensure that our buses create the best conditions for a pleasant journey.',
  },
  recsList: {
    ru: [
      'Рекомендуем покупать билеты заранее на сайте Lux Express или в мобильном приложении.',
      'Пожалуйста, предоставьте соответствующие документы, подтверждающие льготу.',
      'В автобусах запрещены употребление алкоголя и курение.',
      'Для обеспечения безопасности себя и других пассажиров ремни безопасности должны быть пристёгнуты на протяжении всей поездки.',
      'Будьте внимательны к другим пассажирам.',
      'Если Ваши планы изменятся, Вы можете удобно поменять или вернуть билет в личном кабинете.',
    ],
    en: [
      'We recommend purchasing tickets in advance on the Lux Express website or mobile app.',
      'Please provide appropriate documents confirming your discount eligibility.',
      'Alcohol consumption and smoking are prohibited on buses.',
      'For safety, seatbelts must be fastened throughout the journey.',
      'Please be considerate of other passengers.',
      'If your plans change, you can conveniently change or return your ticket online.',
    ],
  },
  popularRoutes: { ru: 'Популярные маршруты', en: 'Popular routes', lv: 'Populāri maršruti', lt: 'Populiarūs maršrutai', pl: 'Popularne trasy' },
  showOnMap: { ru: 'показать на картах Google', en: 'show on Google Maps', lv: 'rādīt Google kartēs', lt: 'rodyti Google žemėlapiuose', pl: 'pokaż na mapach Google' },
};

// Stop data per route
const routeStops = {
  'tallinn-tartu': [
    { name: { ru: 'Таллинн Автобусный вокзал', en: 'Tallinn Bus Station' }, address: 'Lastekodu 46, 10144, Tallinn' },
    { name: { ru: 'Таллиннский аэропорт', en: 'Tallinn Airport' }, address: '' },
    { name: { ru: 'Tartu Naitused', en: 'Tartu Naitused' }, address: '' },
    { name: { ru: 'Тарту Автобусный вокзал', en: 'Tartu Bus Station' }, address: 'Turu 2, 51014, Tartu' },
  ],
  'tartu-narva': [
    { name: { ru: 'Тарту Автобусный вокзал', en: 'Tartu Bus Station' }, address: 'Turu 2, 51014, Tartu' },
    { name: { ru: 'Йыхви Автобусный вокзал', en: 'Jõhvi Bus Station' }, address: 'Narva mnt. 8, 41536, Jõhvi' },
    { name: { ru: 'Силламяэ Автобусный вокзал', en: 'Sillamäe Bus Station' }, address: 'Tallinna 9, 40233, Sillamäe' },
    { name: { ru: 'Нарва Автобусный вокзал', en: 'Narva Bus Station' }, address: 'Vaksali 19, 20308, Narva' },
  ],
  'tallinn-riga': [
    { name: { ru: 'Таллинн Автобусный вокзал', en: 'Tallinn Bus Station' }, address: 'Lastekodu 46, 10144, Tallinn' },
    { name: { ru: 'Пярну Автобусный вокзал', en: 'Pärnu Bus Station' }, address: 'Ringi 3, 80011, Pärnu' },
    { name: { ru: 'Рига Автобусный вокзал', en: 'Riga Coach Station' }, address: 'Pragas iela 1, Rīga' },
  ],
  'tallinn-narva': [
    { name: { ru: 'Таллинн Автобусный вокзал', en: 'Tallinn Bus Station' }, address: 'Lastekodu 46, 10144, Tallinn' },
    { name: { ru: 'Раквере Автобусный вокзал', en: 'Rakvere Bus Station' }, address: 'Laada 18a, 44307, Rakvere' },
    { name: { ru: 'Йыхви Автобусный вокзал', en: 'Jõhvi Bus Station' }, address: 'Narva mnt. 8, 41536, Jõhvi' },
    { name: { ru: 'Силламяэ Автобусный вокзал', en: 'Sillamäe Bus Station' }, address: 'Tallinna 9, 40233, Sillamäe' },
    { name: { ru: 'Нарва Автобусный вокзал', en: 'Narva Bus Station' }, address: 'Vaksali 19, 20308, Narva' },
  ],
  'riga-vilnius': [
    { name: { ru: 'Рига Автобусный вокзал', en: 'Riga Coach Station' }, address: 'Pragas iela 1, Rīga' },
    { name: { ru: 'Бауска', en: 'Bauska' }, address: '' },
    { name: { ru: 'Паневежис Автобусный вокзал', en: 'Panevėžys Bus Station' }, address: '' },
    { name: { ru: 'Вильнюс Автобусный вокзал', en: 'Vilnius Bus Station' }, address: 'Sodų g. 22, Vilnius' },
  ],
  'vilnius-warsaw': [
    { name: { ru: 'Вильнюс Автобусный вокзал', en: 'Vilnius Bus Station' }, address: 'Sodų g. 22, Vilnius' },
    { name: { ru: 'Каунас Автобусный вокзал', en: 'Kaunas Bus Station' }, address: 'Vytauto pr. 24, Kaunas' },
    { name: { ru: 'Сувалки', en: 'Suwałki' }, address: 'Dworzec PKS, Suwałki' },
    { name: { ru: 'Варшава Zachodnia', en: 'Warsaw Zachodnia' }, address: 'Al. Jerozolimskie 144, Warszawa' },
  ],
  'tallinn-haapsalu': [
    { name: { ru: 'Таллинн Автобусный вокзал', en: 'Tallinn Bus Station' }, address: 'Lastekodu 46, 10144, Tallinn' },
    { name: { ru: 'Хаапсалу Автобусный вокзал', en: 'Haapsalu Bus Station' }, address: 'Raudtee 2, 90504, Haapsalu' },
  ],
  'tallinn-hiiumaa': [
    { name: { ru: 'Таллинн Автобусный вокзал', en: 'Tallinn Bus Station' }, address: 'Lastekodu 46, 10144, Tallinn' },
    { name: { ru: 'Таллинн, Вана-Пяскула', en: 'Tallinn, Vana-Pääsküla' }, address: 'Pärнусское шоссе, Tallinn' },
    { name: { ru: 'Хаапсалу Автобусный вокзал', en: 'Haapsalu Bus Station' }, address: 'Raudtee 2, 90504, Haapsalu' },
    { name: { ru: 'Käina', en: 'Käina' }, address: '' },
    { name: { ru: 'Kärdla bussijaam', en: 'Kärdla Bus Station' }, address: '' },
  ],
  'riga-daugavpils': [
    { name: { ru: 'Рига Автобусный вокзал', en: 'Riga Bus Station' }, address: 'Pragas iela 1, Rīga' },
    { name: { ru: 'Даугавпилс Автобусный вокзал', en: 'Daugavpils Bus Station' }, address: 'Viestura iela 2, Daugavpils' },
  ],
  'riga-liepaja': [
    { name: { ru: 'Рига Автобусный вокзал', en: 'Riga Bus Station' }, address: 'Pragas iela 1, Rīga' },
    { name: { ru: 'Салдус', en: 'Saldus' }, address: '' },
    { name: { ru: 'Скрунда', en: 'Skrunda' }, address: '' },
    { name: { ru: 'Лиепая Автобусный вокзал', en: 'Liepāja Bus Station' }, address: 'Rīgas iela 51, Liepāja' },
  ],
  'tallinn-parnu': [
    { name: { ru: 'Таллинн Автобусный вокзал', en: 'Tallinn Bus Station' }, address: 'Lastekodu 46, 10144, Tallinn' },
    { name: { ru: 'Пярну Автобусный вокзал', en: 'Pärnu Bus Station' }, address: 'Ringi 3, 80011, Pärnu' },
  ],
  'tallinn-kuressaare': [
    { name: { ru: 'Таллинн Автобусный вокзал', en: 'Tallinn Bus Station' }, address: 'Lastekodu 46, 10144, Tallinn' },
    { name: { ru: 'Виртсу', en: 'Virtsu' }, address: '' },
    { name: { ru: 'Курессааре Автобусный вокзал', en: 'Kuressaare Bus Station' }, address: 'Pihtla tee 2, Kuressaare' },
  ],
  'riga-kaunas': [
    { name: { ru: 'Рига Автобусный вокзал', en: 'Riga Bus Station' }, address: 'Pragas iela 1, Rīga' },
    { name: { ru: 'Каунас Автобусный вокзал', en: 'Kaunas Bus Station' }, address: 'Vytauto pr. 24, Kaunas' },
  ],
  'tallinn-vilnius': [
    { name: { ru: 'Таллинн Автобусный вокзал', en: 'Tallinn Bus Station' }, address: 'Lastekodu 46, 10144, Tallinn' },
    { name: { ru: 'Пярну Автобусный вокзал', en: 'Pärnu Bus Station' }, address: 'Ringi 3, 80011, Pärnu' },
    { name: { ru: 'Рига Автобусный вокзал', en: 'Riga Bus Station' }, address: 'Pragas iela 1, Rīga' },
    { name: { ru: 'Вильнюс Автобусный вокзал', en: 'Vilnius Bus Station' }, address: 'Sodų g. 22, Vilnius' },
  ],
  'tallinn-warsaw': [
    { name: { ru: 'Таллинн Автобусный вокзал', en: 'Tallinn Bus Station' }, address: 'Lastekodu 46, 10144, Tallinn' },
    { name: { ru: 'Рига Автобусный вокзал', en: 'Riga Bus Station' }, address: 'Pragas iela 1, Rīga' },
    { name: { ru: 'Вильнюс Автобусный вокзал', en: 'Vilnius Bus Station' }, address: 'Sodų g. 22, Vilnius' },
    { name: { ru: 'Варшава Zachodnia', en: 'Warsaw Zachodnia' }, address: 'Al. Jerozolimskie 144, Warszawa' },
  ],
  'tallinn-helsinki': [
    { name: { ru: 'Таллинн Автобусный вокзал', en: 'Tallinn Bus Station' }, address: 'Lastekodu 46, 10144, Tallinn' },
    { name: { ru: 'Хельсинки', en: 'Helsinki' }, address: 'Kamppi Bus Station, Helsinki' },
  ],
  'tallinn-spb': [
    { name: { ru: 'Таллинн Автобусный вокзал', en: 'Tallinn Bus Station' }, address: 'Lastekodu 46, 10144, Tallinn' },
    { name: { ru: 'Нарва', en: 'Narva' }, address: '' },
    { name: { ru: 'Санкт-Петербург', en: 'St. Petersburg' }, address: 'Балтийский вокзал' },
  ],
  'kaunas-warsaw': [
    { name: { ru: 'Каунас Автобусный вокзал', en: 'Kaunas Bus Station' }, address: 'Vytauto pr. 24, Kaunas' },
    { name: { ru: 'Сувалки', en: 'Suwałki' }, address: '' },
    { name: { ru: 'Варшава Zachodnia', en: 'Warsaw Zachodnia' }, address: 'Al. Jerozolimskie 144, Warszawa' },
  ],
};

// Related routes per city
const relatedRoutes = {
  tallinn: ['tartu', 'narva', 'parnu', 'riga', 'vilnius', 'kuressaare', 'haapsalu', 'viljandi', 'rakvere', 'warsaw', 'helsinki'],
  riga: ['tallinn', 'vilnius', 'kaunas', 'warsaw', 'daugavpils', 'liepaja', 'parnu', 'panevezys', 'spb'],
  vilnius: ['riga', 'kaunas', 'warsaw', 'tallinn', 'panevezys', 'suwalki'],
  tartu: ['tallinn', 'narva', 'parnu', 'viljandi'],
  warsaw: ['vilnius', 'kaunas', 'riga', 'tallinn', 'suwalki'],
};

const RouteDetailPage = () => {
  const { fromId, toId } = useParams();
  const navigate = useNavigate();
  const { t, lang } = useLanguage();
  const { setSearchParams } = useBooking();

  useEffect(() => { window.scrollTo(0, 0); }, [fromId, toId]);

  const fromName = getCityName(fromId, lang);
  const toName = getCityName(toId, lang);
  const bs = getBusStation(lang);
  const g = (obj) => obj[lang] || obj.en || obj.ru || '';
  const routeKey = `${fromId}-${toId}`;
  const reverseKey = `${toId}-${fromId}`;
  const specificDesc = routeSpecificDesc[routeKey] || routeSpecificDesc[reverseKey];
  const desc = specificDesc ? (specificDesc[lang] || specificDesc.en || specificDesc.ru) : (routeDescriptions[lang] || routeDescriptions.en)(fromName, toName);
  const stops = routeStops[`${fromId}-${toId}`] || routeStops[`${toId}-${fromId}`] || [
    { name: { ru: `${fromName}, ${bs}`, en: `${fromName}, Bus Station` }, address: '' },
    { name: { ru: `${toName}, ${bs}`, en: `${toName}, Bus Station` }, address: '' },
  ];
  const related = relatedRoutes[fromId] || relatedRoutes[toId] || [];
  const features = (sectionTitles.features[lang] || sectionTitles.features.en);
  const featuresDesc = (sectionTitles.featuresDesc[lang] || sectionTitles.featuresDesc.en);
  const recsList = (sectionTitles.recsList[lang] || sectionTitles.recsList.en);

  // Set search params for this route
  useEffect(() => {
    setSearchParams(prev => ({
      ...prev,
      from: `${fromName}, ${bs}`,
      to: `${toName}, ${bs}`,
    }));
  }, [fromId, toId, lang]); // eslint-disable-line

  return (
    <>
      {/* Dark hero with mini route map */}
      <section className="bg-[#2a2d35] text-white py-10 md:py-14 relative overflow-hidden">
        <div className="max-w-[1100px] mx-auto px-4 md:px-8 flex gap-8">
          <div className="flex-1 min-w-0">
            <h1 className="text-[28px] md:text-[42px] font-bold mb-4">{fromName} - {toName}</h1>
            <p className="text-[14px] md:text-[16px] text-white/60 max-w-[550px] leading-relaxed mb-8">{desc}</p>
            <SearchForm compact={true} />
          </div>
          {/* Mini route map */}
          <div className="hidden lg:block w-[280px] flex-shrink-0 self-center">
            <MiniRouteMap fromName={fromName} toName={toName} />
          </div>
        </div>
      </section>

      {/* Red divider */}
      <div className="h-1 bg-[#c30909]" />

      {/* Stops */}
      <section className="py-10 md:py-14">
        <div className="max-w-[1200px] mx-auto px-4 md:px-8">
          <h2 className="text-[24px] md:text-[32px] font-semibold text-[#c30909] mb-10">{g(sectionTitles.stops)}</h2>
          {/* Timeline like original */}
          <div className="overflow-x-auto pb-4">
            <div className="flex" style={{ minWidth: stops.length > 3 ? `${stops.length * 180}px` : 'auto' }}>
              {stops.map((stop, idx) => (
                <div key={idx} className="flex-1 relative" style={{ minWidth: '160px' }}>
                  {/* Vertical tick + horizontal line */}
                  <div className="flex items-center">
                    <div className="flex flex-col items-center">
                      <div className={`w-[10px] h-[10px] rounded-full ${
                        idx === 0 || idx === stops.length - 1 ? 'bg-[#333]' : 'bg-white border-[2px] border-[#888]'
                      }`} />
                    </div>
                    {idx < stops.length - 1 && (
                      <div className="flex-1 h-[2px] bg-gray-300" />
                    )}
                  </div>
                  {/* Vertical bar */}
                  <div className={`w-[2px] h-[12px] ${idx === 0 || idx === stops.length - 1 ? 'bg-[#333]' : 'bg-[#ccc]'} ml-[4px]`} />
                  {/* Content */}
                  <div className="pr-6 mt-2">
                    <h3 className={`text-[14px] md:text-[17px] ${idx === 0 || idx === stops.length - 1 ? 'font-bold text-[#222]' : 'font-normal text-[#444]'} mb-1 leading-tight`}>{g(stop.name)}</h3>
                    {stop.address ? (
                      <>
                        <a href={`https://maps.google.com/?q=${encodeURIComponent(stop.address)}`} target="_blank" rel="noreferrer"
                          className="text-[11px] text-[#c30909] hover:underline block mb-0.5">📍 {g(sectionTitles.showOnMap)}</a>
                        <p className="text-[11px] text-[#888] leading-relaxed">{stop.address}</p>
                      </>
                    ) : (
                      <a href={`https://maps.google.com/?q=${encodeURIComponent(g(stop.name))}`} target="_blank" rel="noreferrer"
                        className="text-[11px] text-[#c30909] hover:underline">📍 {g(sectionTitles.showOnMap)}</a>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Seat types */}
      <section className="py-8 md:py-12 bg-[#f5f5f5]">
        <div className="max-w-[1200px] mx-auto px-4 md:px-8">
          <div className="bg-white rounded-xl p-6 md:p-10 flex flex-col md:flex-row gap-6 items-center">
            <div className="md:w-1/3">
              <h2 className="text-[22px] md:text-[28px] font-semibold text-[#c30909] mb-3">{g(sectionTitles.seatTypes)}</h2>
              <p className="text-[14px] text-[#555] leading-relaxed">{g(sectionTitles.seatDesc)}</p>
            </div>
            <div className="flex-1 flex justify-center">
              <img src="/seats.webp" alt="Lux Lounge seat & Lux Express seat" className="max-w-[500px] w-full h-auto" />
            </div>
          </div>
        </div>
      </section>

      {/* Why Lux Express */}
      <section className="py-10 md:py-14">
        <div className="max-w-[1200px] mx-auto px-4 md:px-8">
          <h2 className="text-[22px] md:text-[30px] font-semibold text-[#c30909] mb-8">{g(sectionTitles.whyTitle)}</h2>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            {features.map((f, idx) => (
              <div key={idx}>
                <h3 className="text-[16px] md:text-[18px] font-medium text-[#222] mb-2">{f}</h3>
                <p className="text-[13px] md:text-[14px] text-[#555] leading-relaxed">{featuresDesc[idx]}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Recommendations */}
      <section className="py-10 md:py-14 bg-white">
        <div className="max-w-[1200px] mx-auto px-4 md:px-8">
          <div className="flex flex-col md:flex-row gap-8">
            <div className="md:w-1/3 rounded-xl overflow-hidden">
              <img src="https://luxexpress.fra1.cdn.digitaloceanspaces.com/media/LuxExpress_superior_relax_3_vdnu1TPQe3.jpg" alt="Travel" className="w-full h-[200px] md:h-[300px] object-cover rounded-xl" />
            </div>
            <div className="flex-1">
              <h2 className="text-[22px] md:text-[28px] font-semibold text-[#c30909] mb-3">{g(sectionTitles.recommendations)}</h2>
              <p className="text-[14px] text-[#555] leading-relaxed mb-4">{g(sectionTitles.recsDesc)}</p>
              <ul className="space-y-2">
                {recsList.map((rec, idx) => (
                  <li key={idx} className="flex items-start gap-2 text-[14px] text-[#444]">
                    <span className="text-[#c30909] mt-1">•</span>
                    <span>{rec}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Popular routes */}
      <section className="py-8 md:py-12 bg-[#f5f5f5]">
        <div className="max-w-[1200px] mx-auto px-4 md:px-8">
          <h2 className="text-[20px] md:text-[26px] font-semibold text-[#222] mb-4">{g(sectionTitles.popularRoutes)}</h2>
          <div className="flex flex-wrap gap-2">
            {related.map((cityId) => (
              <button key={cityId}
                onClick={() => navigate(`/route/${fromId}/${cityId}`)}
                className="px-4 py-2 border border-gray-300 rounded-full text-[13px] md:text-[14px] text-[#333] hover:border-[#c30909] hover:text-[#c30909] transition-colors bg-white">
                {fromName} - {getCityName(cityId, lang)}
              </button>
            ))}
          </div>
        </div>
      </section>
    </>
  );
};

export default RouteDetailPage;
